"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

const sections = ["home", "resume", "portfolio", "contact"];
const HEADER_OFFSET = 100;

export default function Header() {
  const t = useTranslations("Components");
  const sectionNames = t.raw("header");

  const [activeSection, setActiveSection] = useState("");
  const [isHeader2Active, setIsHeader2Active] = useState(false);
  const [choiceSection, setChoiceSection] = useState<string | null>(null);
  const choiceSectionRef = useRef(choiceSection);

  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    choiceSectionRef.current = choiceSection;
  }, [choiceSection]);

  // Clear activeSection when leaving homepage
  useEffect(() => {
    const isHomePage = pathname === "/" || pathname.match(/^\/[a-z]{2}$/);
    if (!isHomePage) {
      setActiveSection("");
    }
  }, [pathname]);

  // IntersectionObserver for homepage sections
  useEffect(() => {
    const isHomePage = pathname === "/" || pathname.match(/^\/[a-z]{2}$/);
    if (!isHomePage) return;

    const observers: IntersectionObserver[] = [];

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (!choiceSectionRef.current || choiceSectionRef.current === id) {
              setActiveSection(id);
              setChoiceSection(null);
            }
          }
        },
        {
          rootMargin: `-${HEADER_OFFSET}px 0px 0px 0px`,
          threshold: 0.1,
        }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, [pathname]);

  // Header scroll effect
  useEffect(() => {
    const onScroll = () => setIsHeader2Active(window.scrollY > HEADER_OFFSET);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to section when choiceSection is set (after navigating from another page)
  useEffect(() => {
    if (!choiceSection) return;

    const el = document.getElementById(choiceSection);
    if (!el) return;

    const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - HEADER_OFFSET;

    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    setChoiceSection(null);
  }, [choiceSection, pathname]);

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    event.preventDefault();

    const isHomePage = pathname === "/" || pathname.match(/^\/[a-z]{2}$/);

    if (isHomePage) {
      const el = document.getElementById(id);
      if (!el) return;

      const elementPosition =
        el.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - HEADER_OFFSET;

      setChoiceSection(id);
      choiceSectionRef.current = id;
      setActiveSection(id);

      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    } else {
      // Navigate to home page first, scroll handled in useEffect
      setChoiceSection(id);
      choiceSectionRef.current = id;
      router.push(`/#${id}`, { scroll: false });
    }
  };

  return (
    <header
      className={`fixed top-0 z-50 w-full flex flex-row justify-between text-sm uppercase font-bold text-white px-5 items-center sm:text-xl sm:gap-16 sm:justify-center transition-all ease-in-out duration-500 ${
        isHeader2Active
          ? "py-5 bg-background-grey border-b border-primary-grey"
          : "py-16 bg-gradient-to-b from-background-grey to-transparent border-b border-none"
      }`}
    >
      {sections.map((id, index) => (
        <Link
          key={id}
          href={`/#${id}`}
          passHref
          onClick={(e) => handleClick(e, id)}
          className={`relative hover:text-primary-yellow transition-colors duration-200 ${
            activeSection === id
              ? 'text-primary-yellow after:content-[""] after:absolute after:left-0 after:bottom-[-2px] after:h-0.5 after:w-full after:bg-primary-yellow'
              : ""
          } cursor-pointer`}
        >
          {sectionNames[index]}
        </Link>
      ))}
    </header>
  );
}
